import * as React from "react";
import { useWatch, Control, UseFieldArrayReturn, useFieldArray } from "react-hook-form";
import {
  Grid,
  ActionIcon,
  Title,
  Stack,
  useMantineTheme,
  Flex,
  Group,
  Divider,
  Button,
  Modal,
  Text,
} from "@mantine/core";
import {
  IconAnchor,
  IconChartLine,
  IconDatabase,
  IconDownload,
  IconEdit,
  IconFileUpload,
  IconLinkPlus,
  IconNotification,
  IconPlus,
  IconTrash,
} from "@tabler/icons-react";
import { useDebouncedValue } from "@mantine/hooks";

import { CCheckbox, CSelect, CTextarea, CTextInput } from "@supportinitiative/react-hook-form-mantine";

import { QuerySetType, UAction } from "@supportinitiative/common";
import { QuerySetCVMContext } from "../QuerySetCVMContext";

import { Workflow } from "../../../UseCase/Workflow/WorkflowCVM";

import {
  addEdgeViaPort,
  removeEdge,
  removeNode,
  updateAction,
  workflowInitialState,
  workflowReducer,
} from "../../../components/Workflow/WorkflowUtils";

import AddViaEdge from "../../../components/Workflow/Add/AddViaEdge";
import AddViaGroup from "../../../components/Workflow/Add/AddViaGroup";
import AddViaPort from "../../../components/Workflow/Add/AddViaPort";

import DatasetsCVM from "../../../UseCase/Dataset/DatasetsCVM";
import { columnTypes } from "../../../UseCase/UseCaseUtils";
import { getId } from "../../../utilities";
import ActionRouter from "../Workflow/Action/ActionRouter";
import { SECTIONCOLORS } from "../QuerySetUtils";

const options = [
  { label: "Serial Group", name: "SERIAL_GROUP", icon: <IconLinkPlus /> },
  { label: "Parallel Group", name: "PARALLEL_GROUP", icon: <IconLinkPlus /> },
  { label: "Raw Query", name: "RAW_QUERY", icon: <IconLinkPlus /> },
  { label: "Derived Query", name: "DERIVED_QUERY", icon: <IconLinkPlus /> },
  { label: "Bulk Dataload", name: "BULK_DATALOAD", icon: <IconFileUpload /> },
  { label: "Dataload", name: "DATALOAD", icon: <IconFileUpload /> },
  { label: "Derive Data", name: "DERIVE_DATA", icon: <IconDatabase /> },
  { label: "Status Update", name: "STATUS_UPDATE", icon: <IconEdit /> },
];

const groupOptions = [
  { label: "Anchor Match", name: "ANCHOR_MATCH", icon: <IconAnchor /> },
  { label: "Bulk Dataload", name: "BULK_DATALOAD", icon: <IconFileUpload /> },
  { label: "Dataload", name: "DATALOAD", icon: <IconFileUpload /> },
  { label: "Derive Data", name: "DERIVE_DATA", icon: <IconDatabase /> },
  { label: "Notification", name: "NOTIFICATION", icon: <IconNotification /> },
  { label: "Report", name: "REPORT", icon: <IconDownload /> },
  { label: "Status Update", name: "STATUS_UPDATE", icon: <IconEdit /> },
  { label: "Summary Stats", name: "SUMMARY_STATS", icon: <IconChartLine /> },
];

function Input({
  name,
  inputsArray,
  index,
  control,
}: {
  name: `definition.queries.${number}.inputs`;
  inputsArray: UseFieldArrayReturn<QuerySetType, `definition.queries.${number}.inputs`, "__key">;
  index: number;
  control: Control<QuerySetType>;
}) {
  const { state: querySetFormState } = React.useContext(QuerySetCVMContext);

  const codeValuesArray = useFieldArray({
    control,
    name: `${name}.${index}.codeValues`,
  });

  const [showDetails, setShowDetails] = React.useState(false);

  return (
    <>
      <Grid align="stretch" gutter={"xs"} mb="xs">
        <Grid.Col span={2}>
          <CTextInput
            name={`${name}.${index}.name`}
            control={control}
            showErrorText={false}
            label={index === 0 ? "Name" : ""}
            required
            disabled={querySetFormState.actionOptions.disableAll}
          />
        </Grid.Col>
        <Grid.Col span={2}>
          <CSelect
            name={`${name}.${index}.type`}
            control={control}
            label={index === 0 ? "Type" : ""}
            data={columnTypes}
            showErrorText={false}
            required
            disabled={querySetFormState.actionOptions.disableAll}
          />
        </Grid.Col>
        <Grid.Col span={2}>
          <CTextInput
            name={`${name}.${index}.label`}
            control={control}
            showErrorText={false}
            label={index === 0 ? "Label" : ""}
            disabled={querySetFormState.actionOptions.disableAll}
          />
        </Grid.Col>

        <Grid.Col span={1} style={{ display: "flex", alignItems: "flex-end" }}>
          <Button
            variant="default"
            fullWidth
            onClick={() => {
              setShowDetails(true);
            }}
          >
            Details
          </Button>
        </Grid.Col>

        <Grid.Col span={1} style={{ display: "flex", alignItems: "flex-end" }}>
          <ActionIcon
            size="sm"
            variant="transparent"
            color="gray"
            onClick={() => {
              inputsArray.remove(index);
            }}
            style={{
              display: querySetFormState.currentAction.toUpperCase() === "VIEW" ? "none" : "",
            }}
            disabled={querySetFormState.actionOptions.disableAll}
          >
            <IconTrash />
          </ActionIcon>
        </Grid.Col>
      </Grid>

      <Modal
        centered
        size={"xl"}
        opened={showDetails}
        onClose={() => setShowDetails(!showDetails)}
        title={<Title order={3}>Properties</Title>}
      >
        <Grid align="stretch" gutter={"xs"} mb="xs">
          <Grid.Col span={12}>
            <CTextInput name={`${name}.${index}.id`} control={control} label={"ID"} disabled={true} />
          </Grid.Col>
        </Grid>
        <Grid align="stretch" gutter={"xs"} mb="xs">
          <Grid.Col span={12}>
            <CTextarea
              name={`${name}.${index}.comment`}
              control={control}
              showErrorText={false}
              autosize
              minRows={2}
              maxRows={20}
              label={"Comment"}
              disabled={querySetFormState.actionOptions.disableAll}
            />
          </Grid.Col>
        </Grid>
        <Grid align="stretch" gutter={"xs"} mb="xs">
          {/*           <Grid.Col span={6}>
            <CTextInput
              name={`${name}.${index}.default`}
              control={control}
              showErrorText={false}
              label={"Default"}
              disabled={querySetFormState.actionOptions.disableAll}
            />
          </Grid.Col> */}
          <Grid.Col span={12}>
            <CTextInput
              name={`${name}.${index}.format`}
              control={control}
              showErrorText={false}
              label={"Format"}
              disabled={querySetFormState.actionOptions.disableAll}
            />
          </Grid.Col>
        </Grid>

        <Divider mt="lg" label="Constraints" labelPosition="center" />

        <Grid align="stretch" gutter={"xs"} mb="xs">
          <Grid.Col span={2} style={{ display: "flex", alignItems: "flex-end" }}>
            <CCheckbox
              name={`${name}.${index}.notNull`}
              control={control}
              label="Not Null"
              trueValue="YES"
              falseValue="NO"
              disabled={querySetFormState.actionOptions.disableAll}
            />
          </Grid.Col>
        </Grid>

        <Divider mt="lg" label="Properties" labelPosition="center" />

        <Grid align="stretch" gutter={"xs"} mb="xs">
          <Grid.Col span={12}>
            <CTextarea
              name={`${name}.${index}.componentProps`}
              control={control}
              showErrorText={false}
              autosize
              minRows={2}
              maxRows={20}
              label={"Props"}
              disabled={querySetFormState.actionOptions.disableAll}
            />
          </Grid.Col>
        </Grid>

        <Divider mt="lg" label="Code Values" labelPosition="center" />

        <Flex>
          <Group mb="xs" style={{ alignItems: "center" }}>
            <Text fw={500} style={{ fontSize: "14px" }}>
              Values
            </Text>
            <ActionIcon
              size="sm"
              variant="transparent"
              color="gray"
              onClick={() => {
                codeValuesArray.append({
                  name: "",
                  value: "",
                });
              }}
              disabled={querySetFormState.actionOptions.disableAll}
            >
              <IconPlus />
            </ActionIcon>
          </Group>
        </Flex>

        <Stack gap="0" mb="lg">
          {codeValuesArray.fields.map((field, codeIndex) => {
            return (
              <Grid align="stretch" gutter={"xs"} mb="xs" key={field.id}>
                <Grid.Col span={5}>
                  <CTextInput
                    name={`${name}.${index}.codeValues.${codeIndex}.name`}
                    control={control}
                    disabled={querySetFormState.actionOptions.disableAll}
                    label={codeIndex === 0 ? "Name" : ""}
                  />
                </Grid.Col>
                <Grid.Col span={5}>
                  <CTextInput
                    name={`${name}.${index}.codeValues.${codeIndex}.value`}
                    control={control}
                    disabled={querySetFormState.actionOptions.disableAll}
                    label={codeIndex === 0 ? "Value" : ""}
                  />
                </Grid.Col>
                <Grid.Col span={2} style={{ display: "flex", alignItems: "flex-end" }}>
                  <ActionIcon
                    size="sm"
                    variant="transparent"
                    color="gray"
                    onClick={() => {
                      codeValuesArray.remove(index);
                    }}
                    style={{
                      display: querySetFormState.currentAction.toUpperCase() === "VIEW" ? "none" : "",
                    }}
                    disabled={querySetFormState.actionOptions.disableAll}
                  >
                    <IconTrash />
                  </ActionIcon>
                </Grid.Col>
              </Grid>
            );
          })}
        </Stack>

        <Grid align="stretch" gutter={"xs"} mb="xs">
          <Grid.Col span={2}>
            <Button
              color="teal"
              variant="filled"
              fullWidth
              type="submit"
              onClick={
                () => {
                  setShowDetails(false);
                } // Close the modal on Ok
              }
            >
              Ok
            </Button>
          </Grid.Col>
        </Grid>
      </Modal>
    </>
  );
}

function QueryCVM({
  id,
  name,
  index,
  queriesArray,
  control,
  setActiveTab,
  fromWorkflowName,
}: {
  id: string;
  name: `definition.queries.${number}`;
  index: number;
  queriesArray: UseFieldArrayReturn<QuerySetType, "definition.queries", "__key">;
  //queriesArray: any;
  control: Control<QuerySetType, any>;
  setActiveTab: (index: string) => void;
  fromWorkflowName: `definition.queries.${number}.definition`;
}) {
  const theme = useMantineTheme();

  const { state: querySetFormState, dispatch: querySetFormDispatch } = React.useContext(QuerySetCVMContext);

  const inputsArray = useFieldArray({
    control,
    name: `${name}.inputs`,
    keyName: "__key",
  });

  const inputRulesArray = useFieldArray({
    control,
    name: `${name}.inputRules`,
    keyName: "__key",
  });

  const query = useWatch({
    control,
    name: `definition.queries.${index}`,
  });

  const [debouncedReconName] = useDebouncedValue(query.name, 300);

  const fromWorkflowNodes = useWatch({
    control,
    name: `${fromWorkflowName}.workflow.nodes`,
  });

  /// Workflow ////

  const [workflowState, workflowDispatch] = React.useReducer(workflowReducer, workflowInitialState);

  const workflow = useWatch({
    control,
    name: `${name}.definition.workflow`,
  });

  const cancel = () => {
    workflowDispatch({
      type: "RESET",
      value: {
        action: null,
        via: null,
      },
    });
  };

  const save = (values: any) => {
    const nodes = workflow && workflow.nodes ? workflow.nodes : [],
      edges = workflow && workflow.edges ? workflow.edges : [],
      actions = workflow && workflow.actions ? workflow.actions : [];
    const newWorkflow = updateAction(values, nodes, edges, actions);
    querySetFormState.setValue(`${name}.definition.workflow`, newWorkflow);
    workflowDispatch({
      type: "RESET",
      value: {
        action: null,
        via: null,
      },
    });
  };

  /// Workflow ////

  React.useEffect(() => {
    const node = fromWorkflowNodes.find((n) => n.id === id);
    if (node && node.data && node.data.name !== query.name) {
      const newNodes = fromWorkflowNodes.filter((n) => !(n.id === node.id));
      const newNode = {
        ...node,
        data: {
          ...node.data,
          name: query.name ? query.name : node.id,
          description: query.description ? query.description : node.id,
        },
      };
      newNodes.push(newNode);
      querySetFormState.setValue(`${fromWorkflowName}.workflow.nodes`, newNodes);
    }
  }, [debouncedReconName]);

  const arrayAppend = (action: UAction) => {
    inputsArray.append(action);
  };

  const setValue = (key: string, value: any) => {
    querySetFormState.setValue(key, value);
  };

  return (
    <>
      <Stack
        style={{
          marginTop: theme.spacing.xs,
          // padding: theme.spacing.xs,
          width: "100%",
        }}
      >
        <Stack gap="0" style={{ backgroundColor: SECTIONCOLORS[1], padding: theme.spacing.sm }}>
          <Group align="center" style={{ alignItems: "center" }}>
            <Flex>
              <Title
                mb="xs"
                order={5}
                style={{
                  padding: "0px 5px",
                  backgroundColor: "#16A596",
                  color: "#fff",
                }}
              >
                Details
              </Title>
            </Flex>
            <ActionIcon
              size="sm"
              variant="transparent"
              color="gray"
              onClick={() => {
                queriesArray.remove(index);
                setActiveTab(index === 0 ? "0" : (index - 1).toString());
              }}
              style={{
                display: querySetFormState.currentAction.toUpperCase() === "VIEW" ? "none" : "",
              }}
              disabled={querySetFormState.actionOptions.disableAll}
            >
              <IconTrash />
            </ActionIcon>
          </Group>
          <Grid align="stretch" gutter={"xs"} mb="xs">
            <Grid.Col span={4}>
              <CTextInput
                name={`${name}.name`}
                label="Name"
                aria-label="Name"
                control={control}
                required
                disabled={querySetFormState.actionOptions.disableAll}
              />
            </Grid.Col>
            <Grid.Col span={4}>
              <CTextInput
                name={`${name}.command`}
                label="Command"
                required
                aria-label="Command"
                control={control}
                disabled={querySetFormState.actionOptions.disableAll}
              />
            </Grid.Col>
          </Grid>
        </Stack>

        {/* <DatasetsCVM control={control} name={`definition.queries.${index}.definition`} /> */}
        <Stack gap="0" style={{ backgroundColor: SECTIONCOLORS[1], padding: theme.spacing.sm }}>
          <Group mb="xs" align="center" style={{ alignItems: "center" }}>
            <Flex>
              <Title
                m={0}
                order={5}
                style={{
                  padding: "0px 5px",
                  backgroundColor: "#16A596",
                  color: "#fff",
                }}
              >
                Inputs
              </Title>
            </Flex>
            <ActionIcon
              size="sm"
              variant="transparent"
              color="gray"
              onClick={() => {
                inputsArray.append({
                  name: "",
                  type: "TEXT",
                  label: "",
                  notNull: "NO",
                  id: getId(),
                });
              }}
              style={{
                display: querySetFormState.currentAction.toUpperCase() === "VIEW" ? "none" : "",
              }}
              disabled={querySetFormState.actionOptions.disableAll}
            >
              <IconPlus />
            </ActionIcon>
          </Group>

          {inputsArray.fields.length === 0 ? (
            <Grid align="stretch" gutter={"xs"} mb="xs">
              <Grid.Col span={8}>
                <Divider label="No inputs" labelPosition="left" />
              </Grid.Col>
            </Grid>
          ) : (
            <div
              style={{
                display: "flex",
                flexDirection: "column",
              }}
            >
              <Grid align="stretch" gutter={"xs"}>
                <Grid.Col span={12}>
                  {inputsArray.fields.map((field, inputIndex) => {
                    return (
                      <Input
                        name={`${name}.inputs`}
                        inputsArray={inputsArray}
                        control={control}
                        key={field.__key}
                        index={inputIndex}
                      />
                    );
                  })}
                </Grid.Col>
              </Grid>
            </div>
          )}
        </Stack>

        <Stack gap="0" style={{ backgroundColor: SECTIONCOLORS[1], padding: theme.spacing.sm }}>
          <Group align="center" style={{ alignItems: "center" }}>
            <Flex>
              <Title
                mb="xs"
                order={5}
                style={{
                  padding: "0px 5px",
                  backgroundColor: "#16A596",
                  color: "#fff",
                }}
              >
                Input rules
              </Title>
            </Flex>
            <ActionIcon
              size="sm"
              variant="transparent"
              color="gray"
              onClick={() => {
                inputRulesArray.append({
                  rule: "",
                });
              }}
              style={{
                display: querySetFormState.currentAction.toUpperCase() === "VIEW" ? "none" : "",
              }}
              disabled={querySetFormState.actionOptions.disableAll}
            >
              <IconPlus />
            </ActionIcon>
          </Group>

          {inputRulesArray.fields.length === 0 ? (
            <Grid align="stretch" gutter={"xs"} mb="xs">
              <Grid.Col span={8}>
                <Divider label="No rules" labelPosition="left" />
              </Grid.Col>
            </Grid>
          ) : (
            <Stack gap="0" mb="lg">
              {inputRulesArray.fields.map((field, ruleIndex) => {
                return (
                  <Grid align="stretch" gutter={"xs"} mb="xs" key={field.__key}>
                    <Grid.Col span={7}>
                      <CTextarea
                        name={`definition.queries.${index}.inputRules.${ruleIndex}.rule`}
                        control={control}
                        showErrorText={false}
                        autosize
                        minRows={2}
                        maxRows={20}
                        disabled={querySetFormState.actionOptions.disableAll}
                      />
                    </Grid.Col>

                    <Grid.Col span={1} style={{ display: "flex", alignItems: "flex-end" }}>
                      <ActionIcon
                        size="sm"
                        variant="transparent"
                        color="gray"
                        onClick={() => {
                          inputRulesArray.remove(index);
                        }}
                        style={{
                          display: querySetFormState.currentAction.toUpperCase() === "VIEW" ? "none" : "",
                        }}
                        disabled={querySetFormState.actionOptions.disableAll}
                      >
                        <IconTrash />
                      </ActionIcon>
                    </Grid.Col>
                  </Grid>
                );
              })}
            </Stack>
          )}
        </Stack>

        <Stack gap="0" style={{ backgroundColor: SECTIONCOLORS[1], padding: theme.spacing.sm }}>
          <Flex>
            <Title
              mb="xs"
              order={5}
              c="white"
              py={0}
              px={theme.spacing.xxs}
              style={{
                backgroundColor: theme.colors.emerald[0],
              }}
            >
              Workflow
            </Title>
          </Flex>

          <Flex>
            <Workflow
              allNodes={workflow && workflow.nodes ? workflow.nodes : []}
              allEdges={workflow && workflow.edges ? workflow.edges : []}
              disabled={querySetFormState.actionOptions.disableAll}
              selectedEdge={workflowState.edge ? workflowState.edge : undefined}
              selectedNode={workflowState.node ? workflowState.node : undefined}
              onAddNode={(edge) => {
                workflowDispatch({
                  type: "SET",
                  value: {
                    action: "ADD",
                    via: "EDGE",
                    edge: edge,
                  },
                });
              }}
              onPortDragEnd={(fromNodeId, toNodeId) => {
                if (toNodeId) {
                  const nodes = workflow && workflow.nodes ? workflow.nodes : [],
                    edges = workflow && workflow.edges ? workflow.edges : [],
                    actions = workflow && workflow.actions ? workflow.actions : [];

                  const newWorkflow = addEdgeViaPort({ from: fromNodeId, to: toNodeId }, nodes, edges, actions);
                  querySetFormState.setValue(`${name}.definition.workflow`, newWorkflow);
                } else {
                  workflowDispatch({
                    type: "SET",
                    value: {
                      action: "ADD",
                      via: "PORT",
                      port: {
                        from: fromNodeId,
                        to: toNodeId,
                      },
                    },
                  });
                }
              }}
              onGroupAdd={(node) => {
                // console.log("onGroupAdd", node);
                workflowDispatch({
                  type: "SET",
                  value: {
                    action: "ADD",
                    via: "GROUP",
                    node: node,
                  },
                });
              }}
              onCanvasClick={() => {
                // console.log("onCanvasClick");
                // setSelectedEdge(undefined);
              }}
              onDeleteEdge={(edge) => {
                // console.log("onDeleteEdge");
                const nodes = workflow && workflow.nodes ? workflow.nodes : [],
                  edges = workflow && workflow.edges ? workflow.edges : [],
                  actions = workflow && workflow.actions ? workflow.actions : [];

                const newWorkflow = removeEdge(edge, nodes, edges, actions);
                workflowDispatch({
                  type: "RESET",
                  value: {
                    action: null,
                    via: null,
                  },
                });
                // console.log("onDeleteEdge workflow", newWorkflow);
                querySetFormState.setValue(`${name}.definition.workflow`, newWorkflow);
              }}
              onDeleteNode={(node) => {
                const nodes = workflow && workflow.nodes ? workflow.nodes : [],
                  edges = workflow && workflow.edges ? workflow.edges : [],
                  actions = workflow && workflow.actions ? workflow.actions : [];

                const newWorkflow = removeNode(node, nodes, edges, actions);
                workflowDispatch({
                  type: "RESET",
                  value: {
                    action: null,
                    via: null,
                  },
                });
                // console.log("onDeleteNode workflow", newWorkflow);
                querySetFormState.setValue(`${name}.definition.workflow`, newWorkflow);
              }}
              onNodeClick={(node) => {
                // console.log("onNodeClick", node);
                if (
                  node.data?.type.toUpperCase() === "GROUP" ||
                  node.data?.type.toUpperCase() === "SERIAL_GROUP" ||
                  node.data?.type.toUpperCase() === "PARALLEL_GROUP"
                ) {
                  // do no ttrigger node click for group
                } else {
                  workflowDispatch({
                    type: "SET",
                    value: {
                      action: "VIEW",
                      via: "NODE",
                      node: node,
                    },
                  });
                }
              }}
              onEdgeClick={(edge) => {
                // console.log("onEdgeClick", edge);
                workflowDispatch({
                  type: "SET",
                  value: {
                    action: "VIEW",
                    via: "EDGE",
                    edge: edge,
                  },
                });
              }}
            />
          </Flex>
        </Stack>
      </Stack>

      {workflowState && workflowState.action === "ADD" && workflowState.via === "EDGE" && workflowState.edge && (
        <AddViaEdge
          name={`${name}.definition.workflow`}
          edge={workflowState.edge}
          workflowDispatch={workflowDispatch}
          nodes={workflow && workflow.nodes ? workflow.nodes : []}
          edges={workflow && workflow.edges ? workflow.edges : []}
          actions={workflow && workflow.actions ? workflow.actions : []}
          arrayAppend={arrayAppend}
          setValue={setValue}
          options={options}
        />
      )}

      {workflowState && workflowState.action === "ADD" && workflowState.via === "PORT" && workflowState.port && (
        <AddViaPort
          name={`${name}.definition.workflow`}
          port={workflowState.port}
          workflowDispatch={workflowDispatch}
          nodes={workflow && workflow.nodes ? workflow.nodes : []}
          edges={workflow && workflow.edges ? workflow.edges : []}
          actions={workflow && workflow.actions ? workflow.actions : []}
          arrayAppend={arrayAppend}
          setValue={setValue}
          options={options}
        />
      )}

      {workflowState && workflowState.action === "ADD" && workflowState.via === "GROUP" && workflowState.node && (
        <AddViaGroup
          name={`${name}.definition.workflow`}
          node={workflowState.node}
          workflowDispatch={workflowDispatch}
          nodes={workflow && workflow.nodes ? workflow.nodes : []}
          edges={workflow && workflow.edges ? workflow.edges : []}
          actions={workflow && workflow.actions ? workflow.actions : []}
          setValue={setValue}
          options={groupOptions}
        />
      )}

      {workflowState && workflowState.action === "VIEW" && workflowState.via === "NODE" && workflowState.node && (
        <ActionRouter
          node={workflowState.node}
          query={query}
          save={save}
          cancel={cancel}
          from={"QUERYSET"}
          actions={workflow && workflow.actions ? workflow.actions : []}
        />
      )}
    </>
  );
}

export default QueryCVM;
